<!-- Write — type, and she writes it in footprints.

     THE KEYBOARD RULE: when the keyboard is up you must be able to SEE the
     writing happen, or the whole product is happening off-screen. On focus the
     tray gives up its flex height for a short strip, the switch and buttons
     step aside, and the tray auto-scrolls so the newest print — and her — are
     always in view.

     THE MOTION RULE: she is the only animated thing, and she moves like a
     walker — a small bob mid-stride, a glide between prints, and each print
     stamps IN rather than appearing. All of it steps aside for
     prefers-reduced-motion. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="write" :scroll-y="false">
      <div class="sheet">
        <header class="head">
          <h1>Footprint Scribe</h1>
          <span class="mode">{{ surface }}</span>
        </header>

        <div ref="trayEl" class="tray" :class="surface">
          <div ref="rollEl" class="roll">
            <svg viewBox="0 0 340 470" :aria-label="ariaLabel" role="img">
              <template v-if="surface === 'scroll'">
                <!-- parchment, aged: blotches first, then rules, then rollers -->
                <defs>
                  <radialGradient id="blot"><stop offset="0" stop-color="#c9a96f" stop-opacity="0.25" /><stop offset="1" stop-color="#c9a96f" stop-opacity="0" /></radialGradient>
                  <linearGradient id="page" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="#efe3c2" /><stop offset="0.5" stop-color="#e7d8b2" /><stop offset="1" stop-color="#ddcba0" />
                  </linearGradient>
                </defs>
                <rect x="14" y="20" width="312" height="430" fill="url(#page)" stroke="#c0a87c" stroke-width="1.4" />
                <ellipse cx="70" cy="120" rx="60" ry="34" fill="url(#blot)" />
                <ellipse cx="280" cy="300" rx="70" ry="44" fill="url(#blot)" />
                <ellipse cx="150" cy="420" rx="80" ry="26" fill="url(#blot)" />
                <!-- ragged side edges -->
                <path d="M14,20 C10,60 18,110 13,160 C9,210 17,260 13,310 C10,360 16,410 14,450" fill="none" stroke="#b39a6e" stroke-width="1.2" opacity="0.7" />
                <path d="M326,20 C330,70 322,120 327,170 C331,220 323,270 327,320 C330,370 324,420 326,450" fill="none" stroke="#b39a6e" stroke-width="1.2" opacity="0.7" />
                <g stroke="#c2a97c" stroke-width="1" opacity="0.7">
                  <line v-for="y in ruleLines" :key="'l' + y" :x1="34" :y1="y" :x2="306" :y2="y"></line>
                </g>
                <!-- shadow of the curl under each roller -->
                <rect x="14" y="34" width="312" height="7" fill="#5d4126" opacity="0.12" />
                <rect x="14" y="430" width="312" height="7" fill="#5d4126" opacity="0.10" />
                <!-- rollers and knobs -->
                <rect x="2" y="8" width="336" height="18" rx="9" fill="#8a6740" stroke="#5d4126" stroke-width="1.6" />
                <rect x="2" y="11" width="336" height="4" rx="2" fill="#a07b4c" opacity="0.8" />
                <rect x="2" y="444" width="336" height="18" rx="9" fill="#8a6740" stroke="#5d4126" stroke-width="1.6" />
                <rect x="2" y="447" width="336" height="4" rx="2" fill="#a07b4c" opacity="0.8" />
                <circle cx="9" cy="17" r="8" fill="#a07b4c" stroke="#5d4126" stroke-width="1.5" />
                <circle cx="331" cy="17" r="8" fill="#a07b4c" stroke="#5d4126" stroke-width="1.5" />
                <circle cx="9" cy="453" r="8" fill="#a07b4c" stroke="#5d4126" stroke-width="1.5" />
                <circle cx="331" cy="453" r="8" fill="#a07b4c" stroke="#5d4126" stroke-width="1.5" />
              </template>
              <template v-else>
                <line
                  v-for="y in rakeLines" :key="'r' + y" :x1="0" :y1="y" :x2="340" :y2="y"
                  stroke="#c9b58c" stroke-width="1.3"
                />
              </template>

              <g v-for="t in placed" :key="t.key" :transform="t.at" :opacity="t.opacity">
                <!-- inner group so the stamp-in animation cannot fight the
                     positioning transform above it -->
                <g class="stamp-in">
                  <template v-if="surface === 'sandpit'">
                    <path
                      v-for="(p, i) in t.prints" :key="i" :d="p.d"
                      :transform="p.transform + ' translate(1.1,1.9)'" fill="#e8d9b4"
                    />
                    <path
                      v-for="(p, i) in t.prints" :key="'d' + i" :d="p.d"
                      :transform="p.transform" fill="#7a5a37"
                    />
                  </template>
                  <template v-else>
                    <path
                      v-for="(p, i) in t.prints" :key="i" :d="p.d"
                      :transform="p.transform" fill="#4a3524"
                    />
                  </template>
                </g>
                <text v-if="hints" class="hint" x="0" y="27" text-anchor="middle">{{ t.character }}</text>
              </g>
            </svg>

            <img
              v-if="surface === 'sandpit' && scribeOk" :src="SCRIBE" alt=""
              class="scribe" :class="{ walking }" :style="scribeStyle" @error="scribeOk = false"
            />
          </div>
          <span class="count">{{ countLabel }}</span>
        </div>

        <div class="switch">
          <button type="button" :class="{ on: surface === 'sandpit' }" @click="setSurface('sandpit')">Sandpit</button>
          <button type="button" :class="{ on: surface === 'scroll' }" @click="setSurface('scroll')">Scroll</button>
        </div>

        <input
          class="words" type="text" :value="text" maxlength="120"
          placeholder="Type here and watch her write…"
          @input="onType($event.target.value)"
        />

        <div class="actions">
          <button type="button" class="quiet" @click="rake">{{ clearLabel }}</button>
          <button type="button" class="solid" :disabled="!text.trim()" @click="keep">Save &amp; print</button>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { encode, ensureAlphabet } from '../alphabet.js';
import { addMessage, settings } from '../store.js';

const SCRIBE = '/scribe.png';

const router = useRouter();
const text = ref('');
const shown = ref(0);
const surface = ref('sandpit');
const scribeOk = ref(true);
const trayEl = ref(null);
const rollEl = ref(null);
let timer = null;

// The child's own choices, live from Settings.
const prefs = computed(() => settings.value.find((r) => Number(r.id) > 0) ?? {});
const speedMs = computed(() => Number(prefs.value.writingSpeedMs) || 150);
const hints = computed(() => !!prefs.value.showLetterHints);

onMounted(() => {
  ensureAlphabet();
  if (prefs.value.defaultSurface) surface.value = prefs.value.defaultSurface;
  tick();
});
onUnmounted(() => clearTimeout(timer));

function tick() {
  clearTimeout(timer);
  timer = setTimeout(() => {
    if (shown.value < text.value.length) { shown.value += 1; tick(); }
  }, speedMs.value);
}

function onType(v) {
  text.value = v;
  if (shown.value > v.length) shown.value = v.length;   // deletes take prints with them
  tick();
}

const rake = () => { text.value = ''; shown.value = 0; };
const setSurface = (s) => { surface.value = s; };
const clearLabel = computed(() => (surface.value === 'sandpit' ? 'Rake it over' : 'Fresh scroll'));

function keep() {
  const row = addMessage({ text: text.value, surface: surface.value, script: 'theropoda' });
  router.push({ name: 'message-detail', params: { id: row.id } });
}

// Layout: advance is per-character (side-by-side pairs are wider), rows wrap.
const PITCH = 30;
const placed = computed(() => {
  const items = encode(text.value).slice(0, shown.value);
  const sand = surface.value === 'sandpit';
  const LEFT = sand ? 30 : 44, TOP = sand ? 46 : 64, RIGHT = sand ? 308 : 296;
  const STEP_Y = sand ? 64 : 56;
  const out = [];
  let x = LEFT, y = TOP, n = 0;
  for (const it of items) {
    const w = PITCH * it.advance;
    if (x + w > RIGHT) { x = LEFT; y = Math.min(y + STEP_Y, sand ? 430 : 400); }
    if (it.kind === 'glyph') {
      const sway = sand ? ((n % 2 === 0) ? -3 : 3) : 0;
      out.push({ key: it.key, prints: it.prints, character: it.character,
                 at: `translate(${x + w / 2}, ${y + sway})${sand ? '' : ' scale(0.85)'}`,
                 opacity: 1, cx: x + w / 2, cy: y });
      n += 1;
    }
    x += w;
  }
  return out;
});

const last = computed(() => placed.value[placed.value.length - 1] ?? { cx: 30, cy: 46 });
const walking = computed(() => shown.value < text.value.length);

// She glides between prints; the CSS transition does the walking.
const scribeStyle = computed(() => ({
  left: `${Math.min((last.value.cx + 14) / 340 * 100, 60)}%`,
  top: `${Math.max((last.value.cy - 52) / 470 * 100, 0)}%`,
}));

// Keep the newest print in view — this is what makes typing with the keyboard
// up feel like watching her, rather than typing into the dark.
watch(() => [placed.value.length, surface.value], async () => {
  await nextTick();
  const tray = trayEl.value;
  const roll = rollEl.value;
  if (!tray || !roll) return;
  const px = (last.value.cy / 470) * roll.clientHeight;
  const target = Math.max(0, px - tray.clientHeight * 0.55);
  tray.scrollTo({ top: target, behavior: 'smooth' });
});

const rakeLines = Array.from({ length: 17 }, (_, i) => 26 + i * 27);
const ruleLines = [64, 120, 176, 232, 288, 344, 400];

const countLabel = computed(() => (walking.value ? 'writing…' : placed.value.length ? `${placed.value.length} marks` : ''));
const ariaLabel = computed(() => (text.value
  ? `The message “${text.value}” written as dinosaur footprints on the ${surface.value}.`
  : 'An empty writing surface, waiting.'));
</script>

<style scoped>
.write { --background: var(--fs-paper); }
.sheet {
  display: flex; flex-direction: column; height: 100%;
  background: var(--fs-paper); color: var(--fs-ink);
  font-family: var(--fs-body);
  padding-top: env(safe-area-inset-top, 0px);
}
.head { display: flex; justify-content: space-between; align-items: baseline; padding: 14px 20px 8px; flex: none; }
.head h1 { margin: 0; font-family: var(--fs-serif); font-style: italic; font-weight: 400; font-size: 28px; }
.mode { font-size: 12px; letter-spacing: 0.14em; text-transform: uppercase; color: color-mix(in srgb, var(--fs-ink) 50%, transparent); }

.tray { flex: 1 1 0; min-height: 140px; margin: 4px 16px 0; position: relative; overflow-y: auto; overflow-x: hidden; }
.tray.sandpit { border: 3px solid var(--fs-wood); background: var(--fs-sand); box-shadow: inset 0 2px 14px color-mix(in srgb, var(--fs-sepia) 40%, transparent); }
.tray.scroll { border: 0; background: transparent; }
.roll { position: relative; width: 100%; }
.roll svg { display: block; width: 100%; height: auto; }

.scribe {
  position: absolute; width: 34%; max-width: 150px; pointer-events: none;
  transition: left 300ms cubic-bezier(0.45, 0, 0.25, 1), top 300ms cubic-bezier(0.45, 0, 0.25, 1);
}
.scribe.walking { animation: fs-walk 0.34s ease-in-out infinite; }
@keyframes fs-walk {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  30% { transform: translateY(-3px) rotate(-1.6deg); }
  70% { transform: translateY(-1px) rotate(1.4deg); }
}

.stamp-in { animation: fs-stamp 0.18s ease-out both; transform-box: fill-box; transform-origin: center; }
@keyframes fs-stamp {
  from { transform: scale(1.55); opacity: 0; }
  60% { opacity: 1; }
  to { transform: scale(1); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .scribe, .scribe.walking { transition: none; animation: none; }
  .stamp-in { animation: none; }
}

.hint { font-family: var(--fs-body); font-size: 9px; fill: #3f2d1d; opacity: 0.45; }

.count { position: sticky; bottom: 6px; float: right; margin-right: 10px; font-family: var(--fs-serif); font-style: italic; font-size: 15px; color: color-mix(in srgb, var(--fs-ink) 55%, transparent); }

.switch { display: flex; margin: 12px 16px 0; border: 1px solid color-mix(in srgb, var(--fs-sepia) 55%, transparent); flex: none; }
.switch button { flex-grow: 1; min-height: 46px; border: 0; background: transparent; color: var(--fs-ink); font-family: inherit; font-size: 16px; cursor: pointer; }
.switch button.on { background: var(--fs-sepia); color: var(--fs-cream); }

.words {
  margin: 10px 20px 0; padding: 10px 2px; flex: none;
  font-family: inherit; font-size: 19px; color: var(--fs-ink);
  background: transparent; border: 0; border-bottom: 1px solid color-mix(in srgb, var(--fs-sepia) 45%, transparent); outline: none;
}
.words::placeholder { color: color-mix(in srgb, var(--fs-sepia) 50%, transparent); font-style: italic; }

.actions { display: flex; gap: 10px; padding: 10px 20px calc(14px + env(safe-area-inset-bottom, 0px)); flex: none; }
.actions button { flex-grow: 1; min-height: 48px; font-family: inherit; font-size: 16px; border-radius: 2px; cursor: pointer; }
.actions .quiet { border: 1px solid color-mix(in srgb, var(--fs-sepia) 55%, transparent); background: transparent; color: var(--fs-ink); }
.actions .solid { border: 1px solid var(--fs-sepia); background: var(--fs-sepia); color: var(--fs-cream); }
.actions .solid[disabled] { opacity: 0.45; }

/* Keyboard up: the tray shrinks to a strip that follows the writing, and the
   controls that are not the input step aside.

   min() carries both platforms: in Safari the viewport does NOT resize, so
   --app-height (the visual viewport) is the real limit; in the Capacitor
   webview the viewport DOES resize, --app-height comes out TALLER than the
   container, and using it raw pushed the input below the tab bar. The
   container (100%) wins wherever it is smaller. */
.sheet:focus-within { height: min(var(--app-height, 100%), 100%); }
.sheet:focus-within .tray { flex: 1 1 0; min-height: 100px; }
.sheet:focus-within .switch,
.sheet:focus-within .actions { display: none; }
</style>
